import type { NewsArticle } from '@/content/news/articles'
import { getNewsPath } from '@/content/news/articles'
import { getWhitepaper, getWhitepaperLandingPath } from '@/lib/whitepapers'

export const PELGO_SITE_URL = 'https://www.pelgo.com'

export const DEFAULT_OG_IMAGE_PATH = '/assets/og-pelgo-home.jpg'
export const DEFAULT_OG_IMAGE_WIDTH = 1200
export const DEFAULT_OG_IMAGE_HEIGHT = 630

export type PelgoSeoConfig = {
  title: string
  description: string
  canonicalPath?: string
  ogImagePath?: string
  ogImageWidth?: number
  ogImageHeight?: number
  ogType?: 'website' | 'article'
  publishedAt?: string
}

export const PELGO_HOME_SEO: PelgoSeoConfig = {
  title: 'Pelgo | Outplacement, Severance & Workforce Mobility',
  description:
    'Pelgo re-engineers outplacement and severance for the modern enterprise. Intelligent severance administration, next-gen outplacement, and severance maximization in one platform.',
  canonicalPath: '/',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export const PELGO_CONTACT_SEO: PelgoSeoConfig = {
  title: 'Contact Pelgo | Request a Demo',
  description:
    'Request a demo or contact Pelgo about modern outplacement, severance administration, and workforce mobility for your enterprise.',
  canonicalPath: '/contact',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export const PELGO_WHITEPAPERS_INDEX_SEO: PelgoSeoConfig = {
  title: 'Whitepapers | Pelgo Research',
  description:
    'Research and insights from Pelgo on workforce transitions, outplacement, severance, and the future of work.',
  canonicalPath: '/resources/whitepapers',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export function getPelgoWhitepaperSeo(slug: string): PelgoSeoConfig | undefined {
  const whitepaper = getWhitepaper(slug)
  if (!whitepaper) return undefined

  return {
    title: `${whitepaper.title} | Pelgo`,
    description: whitepaper.shortDescription,
    canonicalPath: getWhitepaperLandingPath(slug),
    ogImagePath: whitepaper.heroBgSrc,
    ogType: 'article',
    publishedAt: whitepaper.publishedAt,
  }
}

export const PELGO_NEWS_INDEX_SEO: PelgoSeoConfig = {
  title: 'News | Pelgo',
  description:
    'Company updates and media coverage from Pelgo on outplacement, severance, and the future of work.',
  canonicalPath: '/news',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export function getPelgoNewsArticleSeo(article: NewsArticle): PelgoSeoConfig {
  return {
    title: `${article.title} | Pelgo`,
    description: article.summary,
    canonicalPath: getNewsPath(article.slug),
    ogImagePath: article.heroImageSrc,
    ogType: 'article',
    publishedAt: article.publishedAt,
  }
}

/** @deprecated Use getPelgoNewsArticleSeo */
export function getPelgoBloombergArticleSeo(
  headline: string,
  dek: string,
  publishedAt: string,
): PelgoSeoConfig {
  return {
    title: `${headline} | Pelgo`,
    description: dek,
    canonicalPath: '/news/bloomberg-searching-for-work',
    ogImagePath: '/assets/news/bloomberg-searching-for-work-hero.png',
    ogType: 'article',
    publishedAt,
  }
}

export const PELGO_PRIVACY_SEO: PelgoSeoConfig = {
  title: 'Privacy Notice | Pelgo',
  description:
    'Pelgo Privacy Notice describing how we collect, use, and protect personal information when you use our website and services.',
  canonicalPath: '/privacy',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export const PELGO_TERMS_SEO: PelgoSeoConfig = {
  title: 'Terms of Use | Pelgo',
  description:
    'Pelgo Terms of Use governing access to our website, platform, and related services.',
  canonicalPath: '/terms-of-service',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export const PELGO_SEVERANCE_PLAYBOOK_SEO: PelgoSeoConfig = {
  title: 'Severance & Termination Law Atlas | Pelgo',
  description:
    'A 50-state guide to US severance and termination law: the federal baseline, state exceptions, mini-WARN laws, final pay deadlines, PTO payout, and separation notices.',
  canonicalPath: '/severance-administration-playbook',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export const PELGO_NOT_FOUND_SEO: PelgoSeoConfig = {
  title: 'Page Not Found | Pelgo',
  description:
    'The page you are looking for could not be found. Visit our homepage or contact us for help.',
  ogImagePath: DEFAULT_OG_IMAGE_PATH,
  ogImageWidth: DEFAULT_OG_IMAGE_WIDTH,
  ogImageHeight: DEFAULT_OG_IMAGE_HEIGHT,
}

export function toAbsoluteUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${PELGO_SITE_URL}${normalized}`
}
